import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DynamicFormModel, DynamicFormService, DynamicInputModel, DynamicSelectModel } from '@ng-dynamic-forms/core';
import { CmsService, GlobalMessageService, GlobalMessageType, TranslationService } from '@spartacus/core';
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ComponentService } from 'src/app/service/component.service';
import { StandardModalService } from 'src/app/service/modal/standard-modal.service';
import {
  MyaccountPersonalInfoService,
  RequestDeliveryAddrWsDTO,
} from 'src/app/service/myaccount/myaccount-personal-info.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { GetStateUserPayload, RequestAccountService } from 'src/app/service/user/request-account.service';

@Component({
  selector: 'app-request-new-delivery-addr-modal',
  templateUrl: './request-new-delivery-addr-modal.component.html',
  styleUrls: ['./request-new-delivery-addr-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class RequestNewDeliveryAddrModalComponent implements OnInit, OnDestroy {
  isDesktop: boolean;
  isDisabled = true;
  afterLoad = true;
  requestNewAddrFormGroup: FormGroup;
  requestNewAddrFormModel: DynamicFormModel;
  currentPageSubscription$: Subscription;
  requestNewAddrFormModelSubscription$: Subscription;
  requestNewAddrFormGroupSubscription$: Subscription;
  errorMessages: any = [];
  cxTabletWidth = this.breakpointService.getSize(BREAKPOINT.xl);

  countryCodeIso: string;
  count = 0;
  stateData$: Observable<any>;

  firstNameInput: DynamicInputModel;
  lastNameInput: DynamicInputModel;
  countrySelect: DynamicSelectModel<string>;
  line1Input: DynamicInputModel;
  line2Input: DynamicInputModel;
  postalInput: DynamicInputModel;
  cityInput: DynamicInputModel;
  stateInput: DynamicSelectModel<string>;
  countyInput: DynamicSelectModel<string>;
  modalComponentInstance: any;

  constructor(
    protected modalService: StandardModalService,
    private messageService: GlobalMessageService,
    private personalInfoSvc: MyaccountPersonalInfoService,
    private formService: DynamicFormService,
    private translation: TranslationService,
    private cmsService: CmsService,
    private componentService: ComponentService,
    private requestAccountService: RequestAccountService,
    private breakpointService: BreakpointService,
    protected cdr: ChangeDetectorRef,
    private storageService: StorageService
  ) {
    this.modalService
      .getModalData()
      .pipe(take(1))
      .subscribe((res) => {
        this.isDesktop = res.isDesktop;
        this.isDisabled = res.isDisabled;
      });

    // DO NOT CHANGE THE ORDER
    const errorTranslations = [
      'firstName_full',
      'lastName_full',
      'emailAddress_full',
      'emailAddress_medium',
      'contactNumber',
      'contactNumberInvalid',
      'deliveryCountry',
      'addressLine1',
      'addressLine2',
      'postalCode',
      'city',
      'postalCode_valid',
      'firstName_valid',
      'lastName_valid',
    ];

    errorTranslations.forEach((translate) => {
      this.translation
        .translate(`error.dynamicform.${translate}`)
        .pipe(take(1))
        .subscribe((errorText) => {
          this.errorMessages.push(errorText);
        });
    });
  }

  ngOnInit() {
    this.currentPageSubscription$ = this.cmsService.getCurrentPage().subscribe((currentPage) => {
      if (currentPage && currentPage.pageId.includes('accountPersonalInfoPage')) {
        this.requestNewAddrFormModelSubscription$ = this.componentService.dynamicFormModel$.subscribe((formModel) => {
          this.updateFormModel(formModel);
        });
        this.requestNewAddrFormGroupSubscription$ = this.componentService.dynamicFormGroup$.subscribe((formGroup) => {
          this.updateFormGroup(formGroup);
        });
      }
    });

    this.formService.detectChanges();
    this.isDesktop = window.innerWidth > this.cxTabletWidth;
  }

  closeModal(reason?: any): void {
    this.storageService.removeSessionStorageItem('fieldChange');
    this.storageService.removeSessionStorageItem('user_input_values');
    this.storageService.removeSessionStorageItem('onbilling');
    this.modalService.closeDialog(reason);
  }

  updateFormGroup(form: any): void {
    this.requestNewAddrFormGroup = form;
    this.setStateValue();
    if (!this.afterLoad) {
      this.isDisabled = !this.requestNewAddrFormGroup.valid;
    }
    this.afterLoad = false;
  }

  updateFormModel(form: any) {
    this.requestNewAddrFormModel = form;

    this.firstNameInput = this.formService.findModelById<DynamicInputModel>(
      'firstName_full',
      this.requestNewAddrFormModel
    );
    if (this.firstNameInput !== null) {
      const hasError = {
        required: this.errorMessages[0],
        pattern: this.errorMessages[12],
      };
      this.firstNameInput.errorMessages = hasError;
    }

    this.lastNameInput = this.formService.findModelById<DynamicInputModel>(
      'lastName_full',
      this.requestNewAddrFormModel
    );
    if (this.lastNameInput !== null) {
      const hasError = {
        required: this.errorMessages[1],
        pattern: this.errorMessages[13],
      };
      this.lastNameInput.errorMessages = hasError;
    }

    this.countrySelect = this.formService.findModelById<DynamicSelectModel<string>>(
      'deliveryCountry',
      this.requestNewAddrFormModel
    );
    if (this.countrySelect !== null) {
      const hasError = {
        required: this.errorMessages[6],
      };
      this.countrySelect.errorMessages = hasError;
    }
    this.line1Input = this.formService.findModelById<DynamicInputModel>('addressLine1', this.requestNewAddrFormModel);
    if (this.line1Input !== null) {
      const hasError = {
        required: this.errorMessages[7],
      };
      this.line1Input.errorMessages = hasError;
      this.line1Input.maxLength = 255;
    }
    this.line2Input = this.formService.findModelById<DynamicInputModel>('addressLine2', this.requestNewAddrFormModel);
    if (this.line2Input !== null) {
      const hasError = {
        required: this.errorMessages[8],
      };
      this.line2Input.errorMessages = hasError;
      this.line2Input.maxLength = 255;
    }
    this.postalInput = this.formService.findModelById<DynamicInputModel>('postalCode', this.requestNewAddrFormModel);
    if (this.postalInput !== null) {
      const hasError = {
        required: this.errorMessages[9],
        pattern: this.errorMessages[11],
      };
      this.postalInput.errorMessages = hasError;
    }
    this.cityInput = this.formService.findModelById<DynamicInputModel>('city', this.requestNewAddrFormModel);
    if (this.cityInput !== null) {
      const hasError = {
        required: this.errorMessages[10],
      };
      this.cityInput.errorMessages = hasError;
    }
    this.stateInput = this.formService.findModelById<DynamicSelectModel<string>>('state', this.requestNewAddrFormModel);

    this.formService.detectChanges();
  }

  onSubmitReuestDeliveryAddrForm(): void {
    const RDAFormDetails: RequestDeliveryAddrWsDTO = {
      firstName: '',
      lastName: '',
      country: {
        isocode: '',
        name: '',
      },
      line1: '',
      line2: '',
      postalCode: '',
      town: '',
      district: '',
    };
    if (this.requestNewAddrFormModel.length > 0) {
      this.requestNewAddrFormModel.forEach((field) => {
        const prop = this.formService.findModelById<any>(field.id, this.requestNewAddrFormModel);
        if (prop !== null) {
          switch (field.id) {
            case 'firstName_full':
              RDAFormDetails.firstName = prop.value;
              break;
            case 'lastName_full':
              RDAFormDetails.lastName = prop.value;
              break;
            case 'deliveryCountry':
              RDAFormDetails.country = { isocode: prop.value, name: prop.value };
              break;
            case 'addressLine1':
              RDAFormDetails.line1 = prop.value;
              break;
            case 'addressLine2':
              RDAFormDetails.line2 = prop.value;
              break;
            case 'postalCode':
              RDAFormDetails.postalCode = prop.value;
              break;
            case 'city':
              RDAFormDetails.town = prop.value;
              break;
            case 'state':
              if (prop.value !== null && prop.value !== '') {
                RDAFormDetails.region = { countryIso: RDAFormDetails.country.isocode, isocode: prop.value };
              }
              break;
            case 'county':
              RDAFormDetails.district = prop.value;
              break;
          }
        }
      });

      this.personalInfoSvc.postRequestDeliveryAddrForm(RDAFormDetails).subscribe(
        (success) => {
          if (success.status === 200 || success.status === 202) {
            this.storageService.removeSessionStorageItem('fieldChange');
            this.storageService.removeSessionStorageItem('user_input_values');
            this.storageService.removeSessionStorageItem('onbilling');
            this.closeModal({ isPending: true });
            this.showGlobalMessage(
              'personalInfo.requestNewDeliveryAddr.successMessage',
              GlobalMessageType.MSG_TYPE_CONFIRMATION
            );
          }
        },
        (error) => {
          // catch error
        }
      );
    }
  }

  setStateValue(): any {
    const optionStateValues: any = [];
    if (
      this.requestNewAddrFormGroup.get('deliveryCountry') &&
      typeof this.requestNewAddrFormGroup.get('deliveryCountry') !== 'undefined'
    ) {
      this.countryCodeIso = this.requestNewAddrFormGroup.get('deliveryCountry').value;
    } else {
      this.countryCodeIso = null;
    }

    if (typeof this.countryCodeIso === 'undefined' || this.countryCodeIso === null) {
      this.count = 0;
      return;
    }

    if (this.countryCodeIso !== null || typeof this.countryCodeIso !== 'undefined') {
      const getStateUserPayload: GetStateUserPayload = {
        fields: 'DEFAULT',
        countryIsoCode: this.countryCodeIso,
      };
      this.stateData$ = this.requestAccountService.getStateForRequestAccount(getStateUserPayload);
      this.stateData$.pipe(take(1)).subscribe((data: any) => {
        if (data.items.length > 0) {
          this.count = data.items.length;
          for (const item of data.items) {
            const options = {
              label: item.name,
              value: item.code,
            };
            optionStateValues.push(options);
          }
        } else {
          this.count = 0;
        }

        if (this.stateInput !== null) {
          this.stateInput.options = optionStateValues;
          this.formService.detectChanges();
        }
      });
    }
  }

  private showGlobalMessage(text: string, messageType: GlobalMessageType) {
    this.messageService.add({ key: text }, messageType);
    const element: HTMLElement = document.getElementsByClassName('BottomHeaderSlot')[0] as HTMLElement;
    element.scrollIntoView();
  }
  @HostListener('window:resize')
  onResize(): void {
    if (window.innerWidth > this.cxTabletWidth) {
      this.isDesktop = true;
    } else {
      this.isDesktop = false;
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    if (this.currentPageSubscription$) {
      this.currentPageSubscription$.unsubscribe();
    }
    if (this.requestNewAddrFormModelSubscription$) {
      this.requestNewAddrFormModelSubscription$.unsubscribe();
    }
    if (this.requestNewAddrFormGroupSubscription$) {
      this.requestNewAddrFormGroupSubscription$.unsubscribe();
    }
  }
}
